import { getNormalizedVersion } from 'mbs-ui-shared@utils/get-normalized-version';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';
import { BuildType } from './BuildType.enum';

export type Build = {
  name: string;
  mbsBuildType: BuildType;
  buildType?: string;
  iconImage: string;
  description?: string;
  buildViewOrder?: number;
  downloadsBuildType?: number;
  buildOs: BuildOsType;
  buildLabel: string;
  enabled?: boolean;
  sandbox?: BuildDetails;
  public?: BuildDetails;
  beta?: BuildBetaDetails;
  applicationId: string;
};

export type ConnectFromBuild = {
  buildOs: BuildOsType;
  version: string;
  downloadLink: string;
};

export type BuildDetails = {
  version: string;
  downloadLink: string;
  newVersionAvailableTitle: string;
  isNewerVersionAvailable: boolean;
  availableVersion: string;
};

export type BuildBetaDetails = {
  version: string;
  downloadLink: string;
  isNewerVersionAvailable: boolean;
  isGeneric: boolean;
};

export type BuildsInfo = {
  autoGenerateEnabled: boolean;
  autoUpdateEnabled: boolean;
  betaAvailable?: boolean;
  sandBoxAvailable: boolean;
  buildErrorMessage: string;
  noteOutdated: string;
  isBuildInProgress: boolean;
  settingsButtonVisible: boolean;
  buildsCRUDAvailable: boolean;
  builds: Build[];
  connectFromBuilds: ConnectFromBuild[];
};

export enum BuildOsType {
  Windows = 'Windows',
  Linux = 'Linux',
  Mac = 'Mac',
  MacArm = 'MacArm'
}

export enum BuildVersionType {
  Public = 'public',
  Sandbox = 'sandbox',
  Beta = 'beta'
}

export const getNewBuildSection = (build: Build, useSandbox: boolean): BuildDetails =>
  useSandbox && build?.sandbox ? build.sandbox : build?.public;

const releaseNotesMarks: Record<string, Partial<Record<BuildOsType, string>>> = {
  backup: { [BuildOsType.Windows]: 'wba', [BuildOsType.Linux]: 'lba', [BuildOsType.Mac]: 'mba', [BuildOsType.MacArm]: 'mba' },
  rmm: { [BuildOsType.Windows]: 'wrm', [BuildOsType.Mac]: 'mrm', [BuildOsType.MacArm]: 'mrm' },
  ra: { [BuildOsType.Windows]: 'wc', [BuildOsType.Mac]: 'mc', [BuildOsType.MacArm]: 'mc' }
};

export const getReleaseNotesPath = (applicationId: string, buildOs: BuildOsType, version: string): string => {
  const mark = releaseNotesMarks[applicationId]?.[buildOs];
  const [major, minor, patch, hotfix] = version?.split('.') ?? [];

  if (!mark || !patch) return null;

  const releasePath = `/${mark}-${major}.${minor}.${patch}`;

  return hotfix ? `${releasePath}#${hotfix}` : releasePath;
};

export const SupportCompanyMinimalVersion = {
  [BuildType.CommonWindowsBuild]: '7.9.3.46',
  [BuildType.VMware]: '7.9.3.46',
  [BuildType.LinuxMac]: '4.2.1.110',
  [BuildType.LinuxUbuntu14]: '4.2.1.110',
  [BuildType.LinuxRedHat6]: '4.2.1.110',
  [BuildType.CBLRA]: '3.4.0.28',
  [BuildType.RMMAgentExe]: '2.0.0.115',
  [BuildType.RMMAgentLinuxDeb]: '2.0.0.115',
  [BuildType.RMMAgentLinuxRpm]: '2.0.0.115',
  [BuildType.RMMAgentMac]: '2.0.0.115',
  [BuildType.NetworkDiscovery]: '1.0.0.107'
};

export const getSupportCompanyMinimalVersion = (buildType: BuildType): string => {
  return getNormalizedVersion(SupportCompanyMinimalVersion[buildType]);
};

// Builds in this range are signed with a revoked code sign certificate, so installing them triggers UAC and antivirus warnings
export const RevokedCertificateVersions = { min: '8.0.0.0', max: '8.7.0.222' };

export const revokedCertificateBuildTypes = [BuildType.CommonWindowsBuild, BuildType.VMware];

export const isRevokedCertificateVersion = (version: string): boolean => {
  if (!version) return false;

  return versionCompare(version, RevokedCertificateVersions.min) >= 0 && versionCompare(version, RevokedCertificateVersions.max) <= 0;
};

export const hasRevokedCertificate = (buildType: BuildType, version: string): boolean =>
  revokedCertificateBuildTypes.includes(buildType) && isRevokedCertificateVersion(version);
